/**
 * DublinCoreMeta — Dublin Core metadata tags for enhanced discoverability
 * Now page-specific: adapts DC.subject, DC.type, and DC.coverage per route.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { SITE_URL } from '@/lib/seo';
import { getSEOByRoute } from '@/lib/seo-descriptions';
import BRAND from '@/config/brand';

function setDCMeta(name: string, content: string) {
  const selector = `meta[name="${name}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
  el.setAttribute('data-seo', 'dc');
}

function getPageSubjectAndType(pathname: string): { subject: string; type: string; coverage: string } {
  const p = pathname.replace(/^\/[a-z]{2}\/?/, '/');

  if (/^\/(services\/(call-center|bpo|customer)|call-center|bpo)/.test(p)) {
    return { subject: 'Call Center Outsourcing, BPO, Customer Support, Multilingual Services', type: 'Service', coverage: 'Europe, North America, Middle East, Africa' };
  }
  if (/^\/(services\/(web|it|digital|software|seo)|it-development|web-development|digital-marketing|seo-audit)/.test(p)) {
    return { subject: 'Software Development, Web Development, SaaS, Mobile Apps, Digital Marketing', type: 'Service', coverage: 'Worldwide' };
  }
  if (/^\/(services\/(ai|voice-ai|chatbot)|ai-contact-center)/.test(p)) {
    return { subject: 'AI Contact Center, Voice AI, Chatbots, Intelligent Automation', type: 'Service', coverage: 'Worldwide' };
  }
  if (/^\/industries/.test(p)) {
    const industry = p.replace(/^\/industries\/?/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'All Industries';
    return { subject: `${industry}, BPO, Outsourcing`, type: 'Service', coverage: 'Worldwide' };
  }
  if (/^\/(careers|jobs)/.test(p)) {
    return { subject: 'Jobs, Careers, Recruitment, Call Center Agent, Software Developer', type: 'Collection', coverage: 'Morocco, Spain, UAE, Remote' };
  }
  if (/^\/(marketplace|freelancers?)/.test(p)) {
    return { subject: 'Freelance, Talent Marketplace, Remote Work, IT Freelancers', type: 'Collection', coverage: 'Worldwide' };
  }
  if (/^\/(blog|insights)/.test(p)) {
    return { subject: 'BPO Insights, Outsourcing Trends, Technology Articles', type: 'Text', coverage: 'Worldwide' };
  }
  if (/^\/(pricing|quote|roi)/.test(p)) {
    return { subject: 'BPO Pricing, Outsourcing Costs, ROI Calculator', type: 'InteractiveResource', coverage: 'Worldwide' };
  }
  if (/^\/(contact|start|get-started|order|free-week)/.test(p)) {
    return { subject: 'Contact, Get Started, Free Consultation, BPO Quote', type: 'InteractiveResource', coverage: 'Worldwide' };
  }
  if (/^\/(about|why-morocco|why-us|leadership)/.test(p)) {
    return { subject: 'About Call IT Dev, Morocco Nearshore, Company Overview', type: 'Text', coverage: 'Morocco, Spain, UAE' };
  }
  if (/^\/(case-studies|testimonials)/.test(p)) {
    return { subject: 'Case Studies, Client Success Stories, BPO Results', type: 'Text', coverage: 'Worldwide' };
  }
  if (/^\/(trust-center|security|compliance|legal)/.test(p)) {
    return { subject: 'Security, GDPR Compliance, Data Protection, Trust', type: 'Text', coverage: 'Worldwide' };
  }
  if (/^\/(faq|how-it-works|glossary|guides)/.test(p)) {
    return { subject: 'FAQ, BPO Guide, Outsourcing Glossary, How It Works', type: 'Text', coverage: 'Worldwide' };
  }
  if (/^\/bpo-hub/.test(p)) {
    const city = p.replace(/^\/bpo-hub\/?/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'Global';
    return { subject: `BPO ${city}, Call Center ${city}, Nearshore Outsourcing`, type: 'Service', coverage: city || 'Worldwide' };
  }
  return { subject: 'BPO, Call Center Outsourcing, Digital Factory, Software Development', type: 'Service', coverage: 'Worldwide' };
}

export function DublinCoreMeta() {
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    const seo = getSEOByRoute(location.pathname, language);
    const { subject, type, coverage } = getPageSubjectAndType(location.pathname);

    setDCMeta('DC.title', seo.title);
    setDCMeta('DC.creator', BRAND.name);
    setDCMeta('DC.subject', subject);
    setDCMeta('DC.description', seo.description);
    setDCMeta('DC.publisher', BRAND.name);
    setDCMeta('DC.language', language);
    setDCMeta('DC.format', 'text/html');
    setDCMeta('DC.type', type);
    setDCMeta('DC.identifier', `${SITE_URL}${location.pathname}`);
    setDCMeta('DC.source', SITE_URL);
    setDCMeta('DC.coverage', coverage);
    setDCMeta('DC.rights', BRAND.copyright);
    setDCMeta('DC.date', '2026-03-21');
    setDCMeta('DC.relation', `${SITE_URL}/sitemap.xml`);
  }, [language, location.pathname]);

  return null;
}
